import {RuleParamsMap, MultiParamsRules} from './RuleParamsMap'

export default class SchemaBuilder
{
    static create( ...fields ) {
        return new SchemaBuilder(fields)
    }

    static rules( ...rules ) {
        if( rules.length === 1 && Array.isArray(rules[0]) ) {
            rules = rules[0]
        }

        return rules.map( rule => {
            return rule.$build()
        })
    }

    constructor( fields ) {
        this._ruleSet = []
        this.fields.apply(this, fields)
    }

    // internal

    $build() {
        return this._ruleSet
    }

    // conditions

    fields( ...fields ) {
        if( fields.length === 1 && Array.isArray(fields[0]) ) {
            fields = fields[0]
        }

        if( fields.length === 0 ) {
            fields.push(undefined)
        }

        // it will be `false` on `and`, `or` methods
        if( fields[0] === false && fields.length === 1 ) {
            return this
        }

        this._ruleSet.push(fields)

        return this
    }

    on( ...scenarios ) {
        return this.scenario(...scenarios)
    }

    scenario( ...scenarios ) {
        if( scenarios.length === 1 && Array.isArray(scenarios[0]) ) {
            scenarios = scenarios[0]
        }

        this._ruleSet.push(['scenario', scenarios])
        return this
    }

    and( conditions ) {
        let ruleSetList = conditions( SchemaBuilder.create(false) )

        if( ruleSetList.length === 0 ) {
            console.error('[Validator]: Please provide at least one nested rule to the `and` rule.')
        }

        // because all `and` sub rules are added in same instance, we need only first
        this._ruleSet.push(['and', ruleSetList[0].$build()])
        return this
    }

    or( conditions ) {
        let ruleSetList = conditions( SchemaBuilder.create(false) )

        if( ruleSetList.length === 0 ) {
            console.error('[Validator]: Please provide at least one nested rule to the `or` rule.')
        }

        // because all `or` sub rules are added in same instance, we need only first
        this._ruleSet.push(['or', ruleSetList[0].$build()])
        return this
    }

    each( ...rules ) {
        if( rules.length === 1 && Array.isArray(rules[0]) ) {
            rules = rules[0]
        }

        this._ruleSet.push(['each', SchemaBuilder.rules.apply(null, rules)])
        return this
    }

    not( validator, ...params ) {

        if( validator === 'not' ) {
            console.error('[Validator]: Cannot call "not" validator inside another "not" rule.')
            return this
        }

        if( typeof RuleParamsMap[validator] === 'undefined' ) {
            console.error('[Validator]: Invalid validator name "' + validator + '".')
            return this
        }

        let optionsParamIndex = RuleParamsMap[validator].length

        if( typeof params[optionsParamIndex] === 'undefined' )
            params[optionsParamIndex] = {}

        params[optionsParamIndex].not = true

        this[validator].apply(this, params)
        return this
    }

    when( condition ) {
        if( isArray(condition) && arguments.length === 1 && condition[0] instanceof SchemaBuilder ) {
            condition = SchemaBuilder.rules.apply(null, condition)
        }
        else if( condition instanceof SchemaBuilder ) {
            condition = SchemaBuilder.rules.apply(null, arguments)
        }

        this._ruleSet.push(['when', condition])
        return this
    }

    skipOnEmpty( condition ) {
        this._ruleSet.push(['skipOnEmpty', condition])
        return this
    }

    emptyOn( callback ) {
        this._ruleSet.push(['emptyOn', callback])
        return this
    }

    sync( ...withFields ) {
        if( withFields.length === 1 && Array.isArray(withFields[0]) ) {
            withFields = withFields[0]
        }

        this._ruleSet.push(['sync', withFields])
        return this
    }

    default( value ) {
        this._ruleSet.push(['default', value])
        return this
    }

    dynamic( callback, options = {} ) {
        this._ruleSet.push(['dynamic', callback, options])
        return this
    }

    disabled( options = {} ) {
        this._ruleSet.push(['disabled', [], options])
        return this
    }

    readonly( options = {} ) {
        this._ruleSet.push(['readonly', [], options])
        return this
    }

    visible( options = {} ) {
        this._ruleSet.push(['visible', [], options])
        return this
    }

    /* --- VALIDATORS --- */

    // other validators
    required( invalidateFalse = false, options = {} ) {
        this._ruleSet.push(['required', invalidateFalse, options])
        return this
    }

    match( regex, flags = '', options = {} ) {
        this._ruleSet.push(['match', [regex, flags], options])
        return this
    }

    // boundary validators
    equals( other, isField = false, strict = true, options = {} ) {
        this._ruleSet.push(['equals', [other, isField, strict], options])
        return this
    }

    contains( target, options = {} ) {
        this._ruleSet.push(['contains', target, options])
        return this
    }

    startsWith( target, options = {} ) {
        this._ruleSet.push(['startsWith', target, options])
        return this
    }

    endsWith( target, options = {} ) {
        this._ruleSet.push(['endsWith', target, options])
        return this
    }

    between( from, to, inclusion = true, options = {} ) {
        this._ruleSet.push(['between', [from, to, inclusion], options])
        return this
    }

    in( data, options = {} ) {
        this._ruleSet.push(['in', data, options])
        return this
    }

    has( data, options = {} ) {
        this._ruleSet.push(['has', data, options])
        return this
    }

    hasOne( data, options = {} ) {
        this._ruleSet.push(['hasOne', data, options])
        return this
    }

    minlength( length, options = {} ) {
        this._ruleSet.push(['minlength', length, options])
        return this
    }

    maxlength( length, options = {} ) {
        this._ruleSet.push(['maxlength', length, options])
        return this
    }

    min( min, options = {} ) {
        this._ruleSet.push(['min', min, options])
        return this
    }

    max( max, options = {} ) {
        this._ruleSet.push(['max', max, options])
        return this
    }

    // date & time validators
    date( format = 'YYYY-MM-DD', options = {} ) {
        this._ruleSet.push(['date', format, options])
        return this
    }

    time( format = 'HH:mm', options = {} ) {
        this._ruleSet.push(['time', format, options])
        return this
    }

    datetime( format = 'YYYY-MM-DD HH:mm:ss', options = {} ) {
        this._ruleSet.push(['datetime', format, options])
        return this
    }

    before( target, inclusion = false, format = 'YYYY-MM-DD HH:mm:ss', options = {} ) {
        this._ruleSet.push(['before', [target, inclusion, format], options])
        return this
    }

    after( target, inclusion = false, format = 'YYYY-MM-DD HH:mm:ss', options = {} ) {
        this._ruleSet.push(['after', [target, inclusion, format], options])
        return this
    }

    dateBetween( from, to, inclusivity = '()', format = 'YYYY-MM-DD', units = 'days', options = {} ) {
        this._ruleSet.push(['dateBetween', [from, to, inclusivity, format, units], options])
        return this
    }

    datetimeBetween( from, to, inclusivity = '()', format = 'YYYY-MM-DD HH:mm:ss', units = 'milliseconds', options = {} ) {
        this._ruleSet.push(['datetimeBetween', [from, to, inclusivity, format, units], options])
        return this
    }

    // string & number validators
    alpha( locale = null, options = {} ) {
        this._ruleSet.push(['alpha', locale, options])
        return this
    }

    alphadash( locale = null, options = {} ) {
        this._ruleSet.push(['alphadash', locale, options])
        return this
    }

    alphanumeric( locale = null, options = {} ) {
        this._ruleSet.push(['alphanumeric', locale, options])
        return this
    }

    alphaspaces( locale = null, options = {} ) {
        this._ruleSet.push(['alphaspaces', locale, options])
        return this
    }

    decimal( decimals = '*', separator = '.', options = {} ) {
        this._ruleSet.push(['decimal', [decimals, separator], options])
        return this
    }

    digits( length, options = {} ) {
        this._ruleSet.push(['digits', length, options])
        return this
    }

    integer( options = {} ) {
        this._ruleSet.push(['integer', [], options])
        return this
    }

    numeric( options = {} ) {
        this._ruleSet.push(['numeric', [], options])
        return this
    }

    // custom validators
    email( options = {} ) {
        this._ruleSet.push(['email', [], options])
        return this
    }

    ip( version = 4, options = {} ) {
        this._ruleSet.push(['ip', version, options])
        return this
    }

    url( requireProtocol = false, options = {} ) {
        this._ruleSet.push(['url', requireProtocol, options])
        return this
    }
}
